function roundMoney(amount) {
    return Math.round(amount * 100) / 100;
}
function parseDetails(details) {
    if (!details)
        return {};
    if (typeof details === 'string') {
        try {
            const parsed = JSON.parse(details);
            return parsed && typeof parsed === 'object' ? parsed : {};
        }
        catch {
            return {};
        }
    }
    if (typeof details === 'object')
        return details;
    return {};
}
function readLines(raw) {
    if (!Array.isArray(raw))
        return [];
    const lines = [];
    for (const item of raw) {
        if (!item || typeof item !== 'object')
            continue;
        const record = item;
        const amount = Number(record.amount ?? record.feeAmount);
        if (!Number.isFinite(amount) || amount <= 0)
            continue;
        const name = String(record.name || 'Custom fee').trim() || 'Custom fee';
        lines.push({ name, amount: roundMoney(amount) });
    }
    return lines;
}
function sumsMatch(lines, total) {
    const sum = roundMoney(lines.reduce((totalAmount, line) => totalAmount + line.amount, 0));
    return Math.abs(sum - total) <= 0.01;
}
function priceObjectRecords(priceObject) {
    if (Array.isArray(priceObject)) {
        return priceObject.filter((item) => Boolean(item) && typeof item === 'object');
    }
    if (priceObject && typeof priceObject === 'object') {
        return [priceObject];
    }
    return [];
}
function nestedLines(record, key) {
    const nested = record?.[key];
    if (!nested || typeof nested !== 'object')
        return undefined;
    return nested.customFeeLines;
}
function feeAttrs(fee) {
    if (!fee || typeof fee !== 'object')
        return {};
    const record = fee;
    return record.attributes ?? record;
}
function linesFromFeeCatalog(fees, total) {
    if (!Array.isArray(fees) || !fees.length)
        return null;
    const lines = [];
    for (const fee of fees) {
        const attrs = feeAttrs(fee);
        if (String(attrs.option || 'amount').toLowerCase() === 'percent')
            return null;
        const amount = Number(attrs.feeAmount);
        if (!Number.isFinite(amount) || amount <= 0)
            return null;
        const name = String(attrs.name || 'Custom fee').trim() || 'Custom fee';
        lines.push({ name, amount: roundMoney(amount) });
    }
    return sumsMatch(lines, total) ? lines : null;
}
function customFeeNames(fees) {
    if (!Array.isArray(fees))
        return [];
    return fees
        .map((fee) => String(feeAttrs(fee).name || '').trim())
        .filter(Boolean);
}
/**
 * Charged custom fees for an invoice or receipt, one row per fee.
 * Returns nothing when the stored breakdown does not add up to totalFeeAmount,
 * so the caller can keep the combined additional-fee row.
 */
export function getInvoiceCustomFeeLines(order) {
    const total = roundMoney(Number(order?.totalFeeAmount) || 0);
    if (total <= 0)
        return [];
    const details = parseDetails(order?.details);
    const supplemental = details.supplementalCustomFee;
    const candidates = [
        details.customFeeLines,
        details.packageCustomFees,
        supplemental && typeof supplemental === 'object'
            ? supplemental.lines
            : undefined,
        nestedLines(details, 'packageWebsiteFeeSnapshot'),
    ];
    for (const priceObject of priceObjectRecords(order?.priceObject)) {
        candidates.push(priceObject.packageCustomFees);
        candidates.push(nestedLines(priceObject, 'packageWebsiteFeeSnapshot'));
    }
    for (const candidate of candidates) {
        const lines = readLines(candidate);
        if (lines.length && sumsMatch(lines, total))
            return lines;
    }
    const fromCatalog = linesFromFeeCatalog(order?.custom_fees, total);
    if (fromCatalog?.length)
        return fromCatalog;
    const names = customFeeNames(order?.custom_fees);
    if (names.length === 1)
        return [{ name: names[0], amount: total }];
    return [];
}
